import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp as RankingsIcon,
  Analytics as AnalyticsIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import WebsiteSelector from '../../components/common/WebsiteSelector';

// Import the existing page content (we'll render them inside tabs)
// These are lazy-loaded to avoid circular dependencies
const GlobalDashboardContent = React.lazy(() => import('../global-dashboard/GlobalDashboardPage').then(m => ({ default: m.GlobalDashboardPage })));
const RankingsContent = React.lazy(() => import('../rankings/RankingsPage'));
const AnalyticsContent = React.lazy(() => import('../analytics/AnalyticsPage'));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`performance-hub-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const PerformanceHubPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeedIcon sx={{ fontSize: 36, color: '#cc081c' }} />
              Performance Hub
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Monitor your content performance, track rankings, and analyze traffic - all in one place
            </Typography>
          </Box>
          <WebsiteSelector showAddButton={false} />
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': { py: 2 },
            '& .Mui-selected': { color: '#cc081c' },
            '& .MuiTabs-indicator': { bgcolor: '#cc081c' }
          }}
        >
          <Tab icon={<DashboardIcon />} label="Dashboard" iconPosition="start" />
          <Tab icon={<RankingsIcon />} label="Rankings" iconPosition="start" />
          <Tab icon={<AnalyticsIcon />} label="Analytics" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Typography>Loading...</Typography></Box>}>
        <TabPanel value={tabValue} index={0}>
          <GlobalDashboardContent />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <RankingsContent />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <AnalyticsContent />
        </TabPanel>
      </React.Suspense>
    </Box>
  );
};

export default PerformanceHubPage;
